/**
 * WebinarPage — /webinars/:slug
 *
 * Co-branded partner-webinar registration landing. TSB brand shell (TakerShell)
 * + an "in partnership with <partner>" lockup, rendered from the sanitized
 * GET /api/public/webinars/:slug feed. Two-step email-first registration posts
 * to /api/public/webinars/:slug/register. Status-aware: announced → register,
 * live → register + join, replay → watch the replay, done → archived.
 */

import { useEffect, useRef, useState, type FormEvent, type CSSProperties } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import TakerShell from './components/TakerShell';
import { CountrySelect } from './components/CountrySelect';
import { SmsOptIn } from './components/SmsOptIn';
import { injectGtm } from './lib/gtm';
import { fetchWithRetry } from './lib/fetchRetry';
import { getAttributionPayload } from './attribution';
import { recordLandingView } from './api';
import { webinarLanding, type WebinarReplayBlock } from './lib/webinarLanding';

const API_BASE = (import.meta as unknown as { env: Record<string, string> }).env?.VITE_API_BASE_URL ?? '';
// Dark-background (white) TSB wordmark — the same asset TakerNav uses on the dark bar.

interface Speaker { name: string; title?: string | null; bio?: string | null; headshot_url?: string | null; }
interface PartnerBlock {
  slug: string; name: string; logo_url: string | null;
  primary_color: string | null; accent_color: string | null;
  website_url: string | null; blurb: string | null;
}
interface BrandBlock {
  id: string; slug: string; name: string;
  logoUrl: string | null; faviconUrl: string | null; gtmContainerId: string | null;
}
interface WebinarWhen { dayLong: string; timeShort: string; timeRange: string; dateTimeFull: string; duration: string; }
interface Webinar {
  slug: string; title: string; banner_excerpt: string | null; body_html: string | null;
  hero_kicker?: string | null; hero_headline?: string | null;
  thankyou_html: string | null;
  speakers: Speaker[]; start_date: string | null; start_utc: string | null; timezone: string | null; duration_minutes: number;
  status: 'announced' | 'live' | 'replay' | 'done'; replay_url: string | null;
  /** EE-owned lifecycle (effectiveWebinarReplayPhase). Wins over `status`. */
  replay?: WebinarReplayBlock | null;
  when: WebinarWhen | null; joinUrl: string | null;
  partner: PartnerBlock | null; brand: BrandBlock | null;
  /** What registering signs you up for (stepped unsubscribe plan §3). The
   *  worker writes it; the same words are recorded as consent evidence. */
  consent_fine_print?: string | null;
  whats_happening_label?: string | null;
}

type PageState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; webinar: Webinar };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Two-step email-first registration (mirrors the calls RsvpCard). */
function RegisterCard({ slug, accent, finePrint, whatsHappeningLabel, onDone }: {
  slug: string; accent: string; finePrint?: string | null; whatsHappeningLabel?: string | null; onDone?: (email: string | null) => void;
}) {
  const storageKey = `webinar-reg:${slug}`;
  const [done, setDone] = useState<boolean>(() => {
    try { return !!window.localStorage.getItem(storageKey); } catch { return false; }
  });
  const [step, setStep] = useState<'email' | 'names'>('email');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [country, setCountry] = useState('');
  const [whatsHappening, setWhatsHappening] = useState(false); // unticked by default: express consent
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function post(body: Record<string, string | boolean>): Promise<{ ok?: boolean; already?: boolean; error?: string }> {
    const r = await fetch(`${API_BASE}/api/public/webinars/${encodeURIComponent(slug)}/register`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...body, attribution: getAttributionPayload() }),
    });
    const d = await r.json().catch(() => ({})) as { ok?: boolean; already?: boolean; error?: string };
    if (!r.ok || !d.ok) throw new Error(d.error ?? 'Could not save your registration — please try again.');
    return d;
  }
  function finish() {
    const em = email.trim().toLowerCase() || null;
    try {
      window.localStorage.setItem(storageKey, new Date().toISOString());
      if (em) window.localStorage.setItem(`webinar-reg-email:${slug}`, em);
    } catch { /* private mode */ }
    setDone(true);
    onDone?.(em);
  }
  async function submitEmail(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setError(null);
    if (!EMAIL_RE.test(email.trim())) { setError('Please enter a valid email address.'); return; }
    setBusy(true);
    try {
      const d = await post({ email: email.trim(), stage: 'email' });
      if (d.already) { finish(); return; }
      setStep('names'); setBusy(false);
    } catch (err) { setError(err instanceof Error ? err.message : String(err)); setBusy(false); }
  }
  async function submitNames(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setError(null);
    if (!firstName.trim()) { setError('Please enter your first name.'); return; }
    if (!lastName.trim()) { setError('Please enter your last name.'); return; }
    setBusy(true);
    try {
      await post({ email: email.trim(), first_name: firstName.trim(), last_name: lastName.trim(), ...(country ? { country } : {}), ...(whatsHappening ? { whats_happening: true } : {}) });
      finish();
    } catch (err) { setError(err instanceof Error ? err.message : String(err)); setBusy(false); }
  }

  const fieldStyle: CSSProperties = {
    width: '100%', boxSizing: 'border-box', padding: '13px 16px', borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.95)',
    fontSize: 15, fontFamily: "'Poppins', sans-serif", color: '#0A0A2E',
  };
  const buttonStyle: CSSProperties = {
    background: accent, color: '#fff', border: 'none', cursor: busy ? 'wait' : 'pointer',
    padding: '14px 20px', borderRadius: 8, fontSize: 15, fontWeight: 700,
    fontFamily: "'Poppins', sans-serif", letterSpacing: 0.2, opacity: busy ? 0.7 : 1,
  };
  const cardStyle: CSSProperties = {
    maxWidth: 420, width: '100%', background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.16)', borderRadius: 14, padding: '24px 26px',
    display: 'flex', flexDirection: 'column', gap: 10,
  };

  if (done) {
    return (
      <div style={{
        maxWidth: 420, width: '100%', background: 'rgba(74,222,128,0.12)',
        border: '1px solid rgba(74,222,128,0.4)', borderRadius: 14, padding: '22px 26px',
      }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#4ade80', marginBottom: 6 }}>
          You're registered — seat saved.
        </div>
        <div style={{ fontSize: 13, color: '#c5ccd6', lineHeight: 1.6 }}>
          Check your inbox for a confirmation with your join link and a calendar invite (it lands
          in your time zone automatically). We'll send reminders before we go live — so you can't miss it.
        </div>
      </div>
    );
  }

  if (step === 'names') {
    return (
      <div style={{ maxWidth: 420, width: '100%' }}>
        <form onSubmit={submitNames} style={cardStyle}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', marginBottom: 2 }}>Seat saved — who's joining?</div>
          <input type="text" placeholder="First name" value={firstName} required autoComplete="given-name"
            autoFocus onChange={(e) => setFirstName(e.target.value)} style={fieldStyle} />
          <input type="text" placeholder="Last name" value={lastName} required autoComplete="family-name"
            onChange={(e) => setLastName(e.target.value)} style={fieldStyle} />
          <CountrySelect value={country} onChange={setCountry} style={fieldStyle} />
          {whatsHappeningLabel && (
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12.5, color: '#c5ccd6', lineHeight: 1.5, cursor: 'pointer' }}>
              <input type="checkbox" checked={whatsHappening} onChange={(e) => setWhatsHappening(e.target.checked)} style={{ marginTop: 3 }} />
              <span>{whatsHappeningLabel}</span>
            </label>
          )}
          {error && <div style={{ fontSize: 13, color: '#fda4af' }}>{error}</div>}
          <button type="submit" disabled={busy} className="cta-btn" style={buttonStyle}>
            {busy ? 'Saving…' : 'Complete registration'}
          </button>
          <div style={{ fontSize: 12, color: '#9ca3af', lineHeight: 1.5 }}>
            Your confirmation email, join link, and calendar invite arrive as soon as you finish.
          </div>
        </form>
        <SmsOptIn
          email={email.trim() || null} firstName={firstName.trim() || null} lastName={lastName.trim() || null}
          source="webinar-reg" sourceDetail={slug} heading="Want a text reminder before we go live?"
        />
      </div>
    );
  }

  return (
    <form onSubmit={submitEmail} style={cardStyle}>
      <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', marginBottom: 2 }}>Save your seat</div>
      <input type="email" placeholder="Email address" value={email} required autoComplete="email"
        onChange={(e) => setEmail(e.target.value)} style={fieldStyle} />
      {error && <div style={{ fontSize: 13, color: '#fda4af' }}>{error}</div>}
      <button type="submit" disabled={busy} className="cta-btn" style={buttonStyle}>
        {busy ? 'Saving…' : 'Register for the webinar'}
      </button>
      <div style={{ fontSize: 12, color: '#9ca3af', lineHeight: 1.5 }}>
        You'll get a confirmation with your join link and a calendar invite, plus reminders before we go live.
        Can't make it live? Register anyway — we'll send everyone the recording afterward.
      </div>
      {finePrint && (
        <div style={{ fontSize: 11.5, color: '#9ca3af', lineHeight: 1.5 }}>{finePrint}</div>
      )}
    </form>
  );
}

/** Collapsed text of an element, ignoring any form control nested inside it
 *  (a wrapping <label> contains its own <input>). */
function labelText(el: Element | null): string {
  if (!el) return '';
  const clone = el.cloneNode(true) as HTMLElement;
  clone.querySelectorAll('input, textarea, select').forEach((n) => n.remove());
  return (clone.textContent ?? '').replace(/\s+/g, ' ').trim().slice(0, 200);
}

/** "ap_ar_handling" -> "Ap ar handling". Last-resort label only. */
function humanizeField(field: string): string {
  const s = field.replace(/[_-]+/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : field;
}

/** The <label> that names this specific option (the text beside a radio). */
function optionLabel(el: HTMLInputElement): string {
  const wrap = el.closest('label');
  if (wrap) return labelText(wrap);
  if (el.id) {
    const esc = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(el.id) : el.id;
    return labelText(el.ownerDocument.querySelector(`label[for="${esc}"]`));
  }
  return '';
}

/** The question this control belongs to, resolved through the accessibility
 *  associations CC's own form markup already uses — aria-labelledby on the
 *  radiogroup, <legend> in a fieldset, or a <label for> on a free-text field.
 *  Never a CC class name: the point is that EE reads whatever CC authored
 *  without EE knowing this webinar's question set. */
function questionLabel(el: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement): string {
  const doc = el.ownerDocument;
  const group = el.closest('[aria-labelledby]');
  const ids = group?.getAttribute('aria-labelledby')?.split(/\s+/).filter(Boolean) ?? [];
  const fromAria = ids.map((id) => labelText(doc.getElementById(id))).filter(Boolean).join(' ');
  if (fromAria) return fromAria.slice(0, 200);

  const legend = el.closest('fieldset')?.querySelector('legend');
  if (legend) return labelText(legend);

  // Free-text controls carry their own question in an associated <label>.
  if (el.id) {
    const esc = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(el.id) : el.id;
    const forLabel = labelText(doc.querySelector(`label[for="${esc}"]`));
    if (forLabel) return forLabel;
  }
  if (el.tagName !== 'INPUT' || (el as HTMLInputElement).type === 'text') {
    const wrap = el.closest('label');
    if (wrap) return labelText(wrap);
  }
  return '';
}

const FREE_TEXT_INPUT_TYPES = new Set(['text', 'email', 'url', 'tel', 'number', 'search']);

export interface PulseAnswerPayload {
  field: string;
  label: string;
  position: number;
  value?: string;
  valueLabel?: string;
  text?: string;
}

/** Serialize a pulse form field-agnostically: whatever inputs CC authored,
 *  carrying the human question + option text the registrant actually read.
 *  EE stores answers keyed by input NAME (migration 0217), so a new webinar's
 *  pulse is a CC content change with no taker or worker deploy. Do NOT
 *  reintroduce a hardcoded field list here — that is what silently dropped
 *  every answer for any webinar but PW01. */
export function serializePulseForm(form: HTMLFormElement): PulseAnswerPayload[] {
  const answers: PulseAnswerPayload[] = [];
  const positions = new Map<string, number>();
  const positionFor = (field: string) => {
    const existing = positions.get(field);
    if (existing !== undefined) return existing;
    const next = positions.size;
    positions.set(field, next);
    return next;
  };

  for (const el of Array.from(form.elements)) {
    if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement)) continue;
    const field = el.name?.trim();
    if (!field) continue;

    if (el instanceof HTMLInputElement && (el.type === 'radio' || el.type === 'checkbox')) {
      if (!el.checked || !el.value) continue;
      answers.push({
        field,
        label: questionLabel(el) || humanizeField(field),
        position: positionFor(field),
        value: el.value,
        valueLabel: optionLabel(el) || el.value,
      });
      continue;
    }

    if (el instanceof HTMLSelectElement) {
      for (const opt of Array.from(el.selectedOptions)) {
        if (!opt.value) continue;
        answers.push({
          field,
          label: questionLabel(el) || humanizeField(field),
          position: positionFor(field),
          value: opt.value,
          valueLabel: (opt.textContent ?? '').trim().slice(0, 200) || opt.value,
        });
      }
      continue;
    }

    if (el instanceof HTMLInputElement && !FREE_TEXT_INPUT_TYPES.has(el.type)) continue;

    const text = el.value?.trim();
    if (!text) continue;
    answers.push({
      field,
      label: questionLabel(el) || humanizeField(field),
      position: positionFor(field),
      text,
    });
  }
  return answers;
}

/**
 * Post-registration confirmation view — renders the CC-authored thankyou_html
 * and wires up the optional pulse survey it may carry (form[data-pw-pulse]).
 * The form's markup posts to a relative /webinars/:slug/pulse action; we
 * intercept submit and send it to the worker API instead, then replace the
 * form with an inline confirmation (no redirect, no dead-end).
 */
function ThankYouSection({ html, slug, email }: { html: string; slug: string; email: string | null }) {
  const ref = useRef<HTMLDivElement>(null);
  const doneKey = `webinar-pulse:${slug}`;

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const form = root.querySelector<HTMLFormElement>('form[data-pw-pulse]');
    if (!form) return;

    const showConfirmation = () => {
      const box = document.createElement('div');
      box.style.cssText = 'padding:18px 22px;border-radius:12px;background:#ecfdf5;border:1px solid #a7f3d0;color:#065f46;font-size:15px;font-weight:600;line-height:1.5;';
      // Speaker-agnostic by default — CC names the speaker in its own copy via
      // data-pw-pulse-thanks. A hardcoded "sent to Ben" is wrong for every
      // webinar after PW01.
      box.textContent = form.getAttribute('data-pw-pulse-thanks')?.trim()
        || 'Thanks — your answers are on their way to our speaker. They shape what gets covered live.';
      form.replaceWith(box);
    };

    // Already answered on this device — don't show the form again.
    try {
      if (window.localStorage.getItem(doneKey)) { showConfirmation(); return; }
    } catch { /* private mode */ }

    const onSubmit = async (e: Event) => {
      e.preventDefault();
      const btn = form.querySelector<HTMLButtonElement>('button[type="submit"], input[type="submit"], button:not([type])');
      if (btn) btn.disabled = true;
      const answers = serializePulseForm(form);
      const body = { answers, email: email ?? undefined };
      try {
        const r = await fetch(`${API_BASE}/api/public/webinars/${encodeURIComponent(slug)}/pulse`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
        });
        const d = await r.json().catch(() => ({})) as { ok?: boolean; error?: string };
        if (!r.ok || !d.ok) throw new Error(d.error ?? 'Could not send your answers — please try again.');
        try { window.localStorage.setItem(doneKey, new Date().toISOString()); } catch { /* private mode */ }
        showConfirmation();
      } catch (err) {
        if (btn) btn.disabled = false;
        let errEl = form.querySelector<HTMLElement>('[data-pw-pulse-error]');
        if (!errEl) {
          errEl = document.createElement('div');
          errEl.setAttribute('data-pw-pulse-error', '');
          errEl.style.cssText = 'color:#b91c1c;font-size:13px;margin-top:10px;';
          form.appendChild(errEl);
        }
        errEl.textContent = err instanceof Error ? err.message : String(err);
      }
    };
    form.addEventListener('submit', onSubmit);
    return () => form.removeEventListener('submit', onSubmit);
  }, [html, slug, email, doneKey]);

  return (
    <section style={{ maxWidth: 760, margin: '0 auto', padding: '48px 24px 24px' }}>
      <div ref={ref} className="webinar-body webinar-thankyou" style={{ fontSize: 16, lineHeight: 1.75, color: '#374151' }}
        dangerouslySetInnerHTML={{ __html: html }} />
    </section>
  );
}

export default function WebinarPage({ thanks = false }: { thanks?: boolean }) {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<PageState>({ phase: 'loading' });
  const [registered, setRegistered] = useState(false);
  const [regEmail, setRegEmail] = useState<string | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);

  // Landing-view beacon. Every other taker funnel page already does this; the
  // webinar page never did, which is why the admin Overview had no top-of-funnel
  // number to show. Fire-and-forget beacon, once per slug — it does not gate
  // rendering and a failure is silent by design.
  useEffect(() => {
    if (!slug) return;
    recordLandingView(slug);
  }, [slug]);

  // Recognize a registration made earlier on this device (same key RegisterCard
  // writes) so a returning visitor lands straight on the confirmation view.
  useEffect(() => {
    if (!slug) return;
    try {
      if (window.localStorage.getItem(`webinar-reg:${slug}`)) setRegistered(true);
      setRegEmail(window.localStorage.getItem(`webinar-reg-email:${slug}`));
    } catch { /* private mode */ }
  }, [slug]);

  useEffect(() => {
    if (!slug) { setState({ phase: 'error', message: 'No webinar specified.' }); return; }
    setState((prev) => (prev.phase === 'ready' ? prev : { phase: 'loading' }));
    fetchWithRetry(`${API_BASE}/api/public/webinars/${encodeURIComponent(slug)}`)
      .then(async (r) => {
        if (!r.ok) throw new Error(r.status === 404 ? 'This webinar could not be found.' : `Request failed (${r.status})`);
        const d = await r.json() as { webinar: Webinar };
        setState({ phase: 'ready', webinar: d.webinar });
      })
      .catch((e: Error) => setState({ phase: 'error', message: e.message }));
  }, [slug, retryNonce]);

  // Title, favicon, GTM — set once the webinar resolves.
  useEffect(() => {
    if (state.phase !== 'ready') return;
    const w = state.webinar;
    document.title = thanks
      ? `You're registered — ${w.title} — The Successful Bookkeeper`
      : `${w.title} — The Successful Bookkeeper`;
    injectGtm(w.brand?.gtmContainerId ?? null);
    if (w.brand?.faviconUrl) {
      let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
      if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
      link.href = w.brand.faviconUrl;
    }
  }, [state, thanks]);

  if (state.phase === 'loading') {
    return (
      <TakerShell>
        <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ color: '#6b7280', fontSize: 14 }}>Loading…</div>
        </div>
      </TakerShell>
    );
  }
  if (state.phase === 'error') {
    return (
      <TakerShell>
        <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#991b1b', fontSize: 15, marginBottom: 16 }}>{state.message}</div>
            <button
              type="button"
              onClick={() => setRetryNonce((n) => n + 1)}
              style={{
                padding: '10px 24px',
                borderRadius: 8,
                border: 'none',
                background: '#F05A28',
                color: '#fff',
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Try again
            </button>
          </div>
        </div>
      </TakerShell>
    );
  }

  const w = state.webinar;
  const partner = w.partner;
  const accent = partner?.accent_color || '#F05A28';
  const speakers = w.speakers ?? [];
  // The Engine's own lifecycle decides this, not CC's status enum, which
  // stays 'announced' after the event (mct_99ebb10c-87a). See lib/webinarLanding.
  const landing = webinarLanding(w);
  const isReplay = landing.mode === 'replay-ready' || landing.mode === 'replay-pending';
  const isDone = landing.mode === 'ended';
  const canRegister = landing.canRegister;
  // CC-approved confirmation view: shown to registered visitors pre-event, in
  // place of the marketing body. Falls back to the generic green card alone
  // while thankyou_html is null (CC asset not approved yet).
  const showThankYou = registered && canRegister && !!w.thankyou_html;

  /* ── CC owns the body; EE contributes chrome ────────────────────────────
   * When Command Centre supplies a full page (body_html on the landing, or
   * thankyou_html on the in-place confirmation swap), that page already has
   * its own hero, its own speaker card and its own partner band. EE rendering
   * a second set around it produced two stacked heroes, the title and date
   * three times over, and the speaker's full bio twice — PW02/Plooto was
   * 3,515px with the only two CTAs 3,017px apart and NOTHING to click through
   * the entire 2,408px body. So when CC supplies the body, EE renders only
   * chrome: the partner lockup, the signup, and the footer.
   *
   * ★ Two separate flags on purpose — they are not the same question:
   *   - ccSuppliesBody gates the SPEAKER card. CC's body carries its own
   *     speaker block in every state, replay included, so EE's is always a
   *     duplicate whenever a CC body is on the page.
   *   - collapseHero additionally requires canRegister. Post-event, EE's
   *     eyebrow ("On-demand replay" / "This webinar has ended") is the only
   *     thing on the page telling the truth — CC's body_html is static and
   *     still says "Free live webinar · Sep 24" forever. Collapsing the hero
   *     after the event would strand the reader on stale framing.
   */
  const ccBody = showThankYou ? w.thankyou_html : w.body_html;
  const ccSuppliesBody = !!ccBody;
  const collapseHero = ccSuppliesBody && canRegister;

  /* ★ Third state: EE OWNS the hero (the "H1 split" layout, approved 2026-09-14).
   *
   * When CC ships hero_headline it has handed the promise over as DATA and
   * dropped its own div.lp-hero from body_html, so EE renders the whole hero:
   * promise + ticket on the left, partner lockup + signup on the right, one
   * eyeline. That is what finally uses the empty half of the band — the slim
   * hero above put the signup in a strip with nothing beside it.
   *
   * Gated on canRegister for the same reason collapseHero is: after the event
   * the state eyebrow is the only honest thing on the page, so a past webinar
   * keeps the full EE hero even if CC populated these fields.
   *
   * ★ Every string here is shared by EVERY partner webinar. Name no topic, no
   * partner and no speaker — same rule that produced the "where you are with
   * AI" bug. The only webinar-specific words are CC's own two fields. */
  const eeOwnsHero = !!w.hero_headline && canRegister && !showThankYou;

  /* Shared between the full and slim hero so the two layouts can never drift.
     The partner lockup is deliberately TSB-logo-free — partner-first co-brand. */
  const lockup = partner ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginBottom: eeOwnsHero ? 22 : collapseHero ? 0 : 28, flexWrap: 'wrap' }}>
      <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, fontWeight: 600, letterSpacing: 0.3 }}>
        In partnership with
      </span>
      {partner.logo_url
        ? <img src={partner.logo_url} alt={partner.name} style={{ height: collapseHero ? 40 : 52, width: 'auto', borderRadius: 5 }} />
        : <span style={{ fontSize: 20, fontWeight: 800, color: '#fff' }}>{partner.name}</span>}
    </div>
  ) : null;


  /* Date ticket for the EE-owned hero, built entirely from EE's own structured
     fields — start_date for the big date, `when` for the rest. No CC markup and
     no hardcoded date anywhere: a reschedule flows through automatically, which
     is the whole reason the repo bans literal dates in JSX. */
  const heroTicket = (() => {
    if (!w.start_date || !w.when) return null;
    const [y, m, d] = w.start_date.split('-');
    const month = new Date(Date.UTC(+y, +m - 1, +d))
      .toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
    const weekday = w.when.dayLong.split(',')[0];
    const cell = (label: string, value: string) => (
      <div key={label}>
        <span style={{ display: 'block', fontSize: 9.5, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)', marginBottom: 2 }}>{label}</span>
        <span style={{ fontSize: 14, fontWeight: 600 }}>{value}</span>
      </div>
    );
    return (
      <div style={{ display: 'flex', alignItems: 'stretch', background: 'rgba(255,255,255,0.055)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14, overflow: 'hidden', maxWidth: 520 }}>
        <div style={{ padding: '16px 22px', textAlign: 'center', background: 'rgba(77,77,255,0.14)', borderRight: '1px solid rgba(255,255,255,0.12)', minWidth: 104 }}>
          <span style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#8B8BFF' }}>{month}</span>
          <span style={{ display: 'block', fontFamily: "'Fraunces', Georgia, serif", fontSize: 40, lineHeight: 1.05, fontWeight: 600 }}>{+d}</span>
          <span style={{ display: 'block', fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>{y}</span>
        </div>
        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 18px', padding: '15px 20px' }}>
          {cell('Day', weekday)}
          {cell('Time', w.when.timeShort)}
          {cell('Length', w.when.duration)}
          {cell('Where', 'Live on Zoom')}
        </div>
      </div>
    );
  })();

  /* id="register" lives here so the legacy #register anchor still resolves. */
  const registerBlock = canRegister ? (
    <div id="register" style={{ display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'stretch' }}>
      {w.status === 'live' && w.joinUrl && (
        <a href={w.joinUrl} target="_blank" rel="noopener noreferrer" className="cta-btn"
          style={{ display: 'block', textAlign: 'center', background: accent, color: '#fff', padding: '14px 24px', borderRadius: 8, fontSize: 15, fontWeight: 700, textDecoration: 'none' }}>
          We're live — join now
        </a>
      )}
      <RegisterCard slug={w.slug} accent={accent} finePrint={w.consent_fine_print} whatsHappeningLabel={w.whats_happening_label} onDone={(em) => {
        setRegistered(true);
        if (em) setRegEmail(em);
        navigate(`/webinars/${w.slug}/thanks`);
      }} />
    </div>
  ) : null;

  // /webinars/:slug/thanks — the confirmation view at its own URL. Registration
  // completion navigates here; the route also renders directly (QA, email links)
  // without requiring a registration on this device. Post-event it redirects to
  // the landing page, where the replay/archived states live.
  if (thanks) {
    if (!canRegister) return <Navigate to={`/webinars/${w.slug}`} replace />;
    return (
      <TakerShell footerLabel={partner ? `In partnership with ${partner.name}` : undefined}>
        <section style={{ background: 'linear-gradient(160deg,#0A0A2E 0%,#141438 100%)', color: '#fff', padding: '56px 24px 64px' }}>
          <div style={{ maxWidth: 1040, margin: '0 auto' }}>
            {partner && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginBottom: 28, flexWrap: 'wrap' }}>
                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, fontWeight: 600, letterSpacing: 0.3 }}>
                  In partnership with
                </span>
                {partner.logo_url
                  ? <img src={partner.logo_url} alt={partner.name} style={{ height: 52, width: 'auto', borderRadius: 5 }} />
                  : <span style={{ fontSize: 20, fontWeight: 800, color: '#fff' }}>{partner.name}</span>}
              </div>
            )}
            {w.when && (
              <div style={{ fontSize: 13, fontWeight: 700, color: accent, letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 14 }}>
                {`Live webinar · ${w.when.dayLong}`}
              </div>
            )}
            <h1 style={{ fontFamily: "'Poppins', sans-serif", fontSize: 40, lineHeight: 1.1, fontWeight: 800, letterSpacing: '-0.02em', marginBottom: 18 }}>
              {w.title}
            </h1>
            {w.when && (
              <div style={{ fontSize: 15, color: 'rgba(255,255,255,0.9)', marginBottom: 26 }}>
                <strong>{w.when.dayLong}</strong> at <strong>{w.when.timeShort}</strong> · {w.when.duration}
              </div>
            )}
            <div style={{
              maxWidth: 420, background: 'rgba(74,222,128,0.12)',
              border: '1px solid rgba(74,222,128,0.4)', borderRadius: 14, padding: '22px 26px',
            }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#4ade80', marginBottom: 6 }}>
                You're registered — seat saved.
              </div>
              <div style={{ fontSize: 13, color: '#c5ccd6', lineHeight: 1.6 }}>
                Check your inbox for a confirmation with your join link and a calendar invite (it lands
                in your time zone automatically). We'll send reminders before we go live — so you can't miss it.
              </div>
              {w.thankyou_html?.includes('data-pw-pulse') && (
                <div style={{ marginTop: 14 }}>
                  {/* ★ Topic-AGNOSTIC as well as speaker-agnostic. This cue renders for
                      EVERY partner webinar that ships a pulse form, so it must never name
                      the subject. It read "…where you are with AI…" from PW01/Dext until
                      2026-09-13, which was simply wrong on PW02 (Plooto, AP/AR payments)
                      and would have been wrong on all six webinars booked behind it.
                      CC's own thankyou_html already introduces the specific questions —
                      this line only has to get the reader down to them. */}
                  <div style={{ fontSize: 13, color: '#c5ccd6', lineHeight: 1.6, marginBottom: 8 }}>
                    Help shape the session — answer a few quick questions and our speaker will build the talk around what you actually need.
                  </div>
                  <button
                    type="button"
                    onClick={() => document.querySelector('form[data-pw-pulse]')?.scrollIntoView({ block: 'start' })}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      background: '#4ade80', color: '#0A0A2E',
                      border: 'none', borderRadius: 999,
                      padding: '10px 18px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
                    }}
                  >
                    Shape the session →
                  </button>
                </div>
              )}
            </div>
            {regEmail && (
              <div style={{ maxWidth: 420 }}>
                <SmsOptIn email={regEmail} firstName={null} lastName={null}
                  source="webinar-reg" sourceDetail={w.slug} collapsible />
              </div>
            )}
          </div>
        </section>
        {w.thankyou_html ? (
          <ThankYouSection html={w.thankyou_html} slug={w.slug} email={regEmail} />
        ) : (
          <section style={{ maxWidth: 760, margin: '0 auto', padding: '48px 24px 56px', textAlign: 'center' }}>
            <a href={`/webinars/${w.slug}`} style={{ color: accent, fontSize: 15, fontWeight: 600 }}>
              Back to the webinar details
            </a>
          </section>
        )}
      </TakerShell>
    );
  }

  return (
    <TakerShell footerLabel={partner ? `In partnership with ${partner.name}` : undefined}>
      {/* Hero — full when EE owns the page, slim chrome when CC supplies the body */}
      <section style={{
        background: eeOwnsHero
          ? 'radial-gradient(ellipse 80% 60% at 12% -15%,rgba(77,77,255,0.38),transparent 62%),'
            + 'radial-gradient(ellipse 60% 55% at 92% 115%,rgba(117,216,255,0.16),transparent 58%),'
            + 'linear-gradient(168deg,#0A0A2E 0%,#060618 100%)'
          : 'linear-gradient(160deg,#0A0A2E 0%,#141438 100%)',
        color: '#fff',
        padding: eeOwnsHero ? '56px 28px 60px' : collapseHero ? '26px 24px 30px' : '56px 24px 64px',
      }}>
        {eeOwnsHero && (
          /* EE's hero must own its own type. Poppins + Fraunces reach the page
             today only because CC's body_html @imports them — but this hero
             exists precisely so CC can stop shipping a hero, so borrowing its
             stylesheet would be a dependency waiting to break. Re-importing the
             same Google Fonts URL is deduped by the browser.
             The media query lives here because inline styles cannot express one,
             and without it the 360px signup column would crush the headline on
             a phone. */
          <style>{`
@import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@1,9..144,400&family=Poppins:wght@400;600;700;900&display=swap');
@media (max-width: 980px) {
  .pw-hero-split { grid-template-columns: 1fr !important; gap: 34px !important; }
}
`}</style>
        )}
        <div style={{ maxWidth: eeOwnsHero ? 1160 : 1040, margin: '0 auto' }}>
          {eeOwnsHero ? (
            /* H1 "split": promise + ticket left, lockup + signup right. */
            <div className="pw-hero-split" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.15fr) 360px', gap: 56, alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#8B8BFF', marginBottom: 22 }}>
                  {partner?.name
                    ? `Free live webinar · ${w.brand?.name ?? 'The Successful Bookkeeper'} × ${partner.name}`
                    : 'Free live webinar'}
                </div>
                <h1 style={{ margin: '0 0 22px', lineHeight: 0.98 }}>
                  {w.hero_kicker && (
                    <span style={{ display: 'block', fontFamily: "'Fraunces', Georgia, serif", fontStyle: 'italic', fontWeight: 400, fontSize: 'clamp(26px,3.1vw,44px)', letterSpacing: '-0.01em', marginBottom: 2 }}>
                      {w.hero_kicker}
                    </span>
                  )}
                  <span style={{ display: 'block', fontFamily: "'Poppins', sans-serif", fontWeight: 900, fontSize: 'clamp(40px,5.4vw,76px)', letterSpacing: '-0.035em', background: 'linear-gradient(92deg,#4D4DFF 0%,#6E6EFF 45%,#8B8BFF 100%)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>
                    {w.hero_headline}
                  </span>
                </h1>
                <p style={{ margin: '0 0 26px', fontSize: 16.5, fontWeight: 600, color: 'rgba(255,255,255,0.95)' }}>
                  <span style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#8B8BFF', marginBottom: 7 }}>The webinar</span>
                  {w.title}
                </p>
                {heroTicket}
              </div>
              <div>
                {lockup}
                {registerBlock}
                <p style={{ margin: '16px 0 0', fontSize: 13, color: 'rgba(255,255,255,0.55)', textAlign: 'center' }}>
                  Free to attend · Bring your questions
                </p>
              </div>
            </div>
          ) : collapseHero ? (
            /* Slim: lockup + signup only. CC's own hero lands immediately below,
               so anything else here is the second hero we just removed. */
            <div style={{ display: 'flex', alignItems: 'center', gap: 32, flexWrap: 'wrap' }}>
              {lockup}
              {registerBlock && (
                <div style={{ marginLeft: 'auto', minWidth: 300, flex: '0 1 340px' }}>{registerBlock}</div>
              )}
            </div>
          ) : (
            <>
              {lockup}
              <div style={{ display: 'grid', gridTemplateColumns: canRegister ? 'minmax(0,1fr) auto' : '1fr', gap: 44, alignItems: 'start' }}>
                <div>
                  {w.when && (
                    <div style={{ fontSize: 13, fontWeight: 700, color: accent, letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 14 }}>
                      {isReplay ? 'On-demand replay' : isDone ? 'This webinar has ended' : `Live webinar · ${w.when.dayLong}`}
                    </div>
                  )}
                  <h1 style={{ fontFamily: "'Poppins', sans-serif", fontSize: 40, lineHeight: 1.1, fontWeight: 800, letterSpacing: '-0.02em', marginBottom: 18 }}>
                    {w.title}
                  </h1>
                  {w.banner_excerpt && (
                    <p style={{ fontSize: 17, lineHeight: 1.6, color: 'rgba(255,255,255,0.8)', maxWidth: 620, marginBottom: 22 }}>
                      {w.banner_excerpt}
                    </p>
                  )}
                  {w.when && !isDone && (
                    <div style={{ fontSize: 15, color: 'rgba(255,255,255,0.9)' }}>
                      <strong>{w.when.dayLong}</strong> at <strong>{w.when.timeShort}</strong> · {w.when.duration}
                    </div>
                  )}
                </div>
                {registerBlock}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Confirmation view — CC-approved thank-you body (may carry the pulse form) */}
      {showThankYou && (
        <ThankYouSection html={w.thankyou_html!} slug={w.slug} email={regEmail} />
      )}

      {/* Body */}
      {!showThankYou && (
      /* ★ The 760px cap is for EE's OWN prose. A CC body brings its own width
         (.pw-lp caps itself at 880px and centres), so capping at 760 squeezed
         it and was part of why it read as a card pasted into the page. */
      <section style={{
        maxWidth: ccSuppliesBody ? 1040 : 760, margin: '0 auto',
        padding: collapseHero ? '18px 24px 24px' : '48px 24px 24px',
      }}>
        {isReplay && (
          <div style={{ background: '#fff', border: '1px solid #e8e3de', borderRadius: 16, padding: '28px 30px', marginBottom: 36, textAlign: 'center' }}>
            <h2 style={{ fontFamily: "'Poppins', sans-serif", fontSize: 22, fontWeight: 700, color: '#0A0A2E', marginBottom: 10 }}>
              {landing.replayHref ? 'The replay is ready' : 'The replay is on its way'}
            </h2>
            <p style={{ fontSize: 15, color: '#4b5563', lineHeight: 1.6, marginBottom: landing.replayHref ? 18 : 0 }}>
              {landing.replayHref
                ? 'Everyone who registered can watch the full session whenever suits them.'
                : "This webinar has ended. We're getting the recording ready, and everyone who registered will get an email the moment it's up."}
            </p>
            {landing.replayHref && (
              <a href={landing.replayHref}
                {...(/^https?:/.test(landing.replayHref) ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                className="cta-btn"
                style={{ display: 'inline-block', background: accent, color: '#fff', padding: '13px 32px', borderRadius: 8, fontSize: 15, fontWeight: 700, textDecoration: 'none' }}>
                Watch the replay
              </a>
            )}
          </div>
        )}
        {isDone && (
          <div style={{ background: '#fff', border: '1px solid #e8e3de', borderRadius: 16, padding: '28px 30px', marginBottom: 36, textAlign: 'center', color: '#4b5563', fontSize: 15 }}>
            This webinar has ended, and the replay is no longer available. Thank you to everyone who joined us.
          </div>
        )}
        {w.body_html && (
          <div className="webinar-body" style={{ fontSize: 16, lineHeight: 1.75, color: '#374151' }}
            dangerouslySetInnerHTML={{ __html: w.body_html }} />
        )}
      </section>
      )}

      {/* Speakers — EE's card only when CC hasn't supplied a body containing its own.
          Gated on ccSuppliesBody (not collapseHero): CC's body carries a speaker
          block in every state, so EE's is a duplicate on replay pages too. */}
      {speakers.length > 0 && !ccSuppliesBody && (
        <section style={{ maxWidth: 900, margin: '0 auto', padding: '12px 24px 56px' }}>
          <h2 style={{ fontFamily: "'Poppins', sans-serif", fontSize: 22, fontWeight: 700, color: '#0A0A2E', marginBottom: 20 }}>
            {speakers.length > 1 ? 'Your speakers' : 'Your speaker'}
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: speakers.length > 1 ? 'repeat(auto-fill, minmax(360px, 1fr))' : '1fr', gap: 18 }}>
            {speakers.map((s) => (
              <div key={s.name} style={{
                background: '#fff', border: '1px solid #e8e3de', borderRadius: 16,
                padding: '28px 26px', display: 'flex', alignItems: 'flex-start', gap: 22,
                boxShadow: '0 2px 12px rgba(10,10,46,0.06)',
              }}>
                {s.headshot_url ? (
                  <img src={s.headshot_url} alt={s.name} style={{ width: 88, height: 88, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: `3px solid ${accent}` }} />
                ) : (
                  <div style={{
                    width: 88, height: 88, borderRadius: '50%', flexShrink: 0,
                    background: `linear-gradient(135deg,${accent},#0A0A2E)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 28, fontWeight: 700,
                  }}>
                    {s.name.split(/\s+/).map((x) => x[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                )}
                <div>
                  <h3 style={{ fontFamily: "'Poppins', sans-serif", fontSize: 20, fontWeight: 700, color: '#0A0A2E', marginBottom: 3 }}>{s.name}</h3>
                  {s.title && <div style={{ fontSize: 12, fontWeight: 700, color: accent, letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 10 }}>{s.title}</div>}
                  {s.bio && <p style={{ fontSize: 14, color: '#4b5563', lineHeight: 1.65 }}>{s.bio}</p>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Closing conversion block.
       *
       * ★ A CC body ends on its own call to action — PW02 closes with "Save your
       *   seat and bring your questions". What used to follow was an anchor that
       *   scrolled BACK to the top form, which meant the whole body had no way to
       *   convert in place. A real signup here answers the ask where it is made.
       *   RegisterCard is fully self-contained (own state, own localStorage read),
       *   so a second instance is safe and either one completing navigates to
       *   /thanks. Without a CC body, EE's own hero is a short scroll away and the
       *   lighter jump link is still the right thing. */}
      {canRegister && !registered && (
        collapseHero ? (
          <section style={{ background: 'linear-gradient(160deg,#0A0A2E 0%,#141438 100%)', color: '#fff', padding: '52px 24px 58px' }}>
            <div style={{ maxWidth: 520, margin: '0 auto', textAlign: 'center' }}>
              {/* ★ NOT "Save your seat" — RegisterCard's own label already says that
                  directly below, and a CC body typically closes on it too, which
                  would put the same three words three times inside 100px. This
                  heading complements the card instead of repeating it. Keep it
                  topic- and speaker-agnostic: one renderer serves every partner
                  webinar (see the "with AI" cue regression, 2026-09-13). */}
              <h2 style={{ fontFamily: "'Poppins', sans-serif", fontSize: 28, fontWeight: 800, letterSpacing: '-0.02em', marginBottom: 8 }}>
                Come with your questions.
              </h2>
              {w.when && (
                <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.75)', marginBottom: 24 }}>
                  {w.when.dayLong} at {w.when.timeShort} · {w.when.duration} · Free to attend
                </p>
              )}
              <div style={{ maxWidth: 360, margin: '0 auto', textAlign: 'left' }}>
                <RegisterCard slug={w.slug} accent={accent} finePrint={w.consent_fine_print} whatsHappeningLabel={w.whats_happening_label} onDone={(em) => {
                  setRegistered(true);
                  if (em) setRegEmail(em);
                  navigate(`/webinars/${w.slug}/thanks`);
                }} />
              </div>
            </div>
          </section>
        ) : (
          <section style={{ maxWidth: 760, margin: '0 auto', padding: '0 24px 64px', textAlign: 'center' }}>
            <a href="#register" className="cta-btn"
              style={{ display: 'inline-block', background: accent, color: '#fff', padding: '14px 40px', borderRadius: 8, fontSize: 15, fontWeight: 700, textDecoration: 'none' }}>
              Register for the webinar
            </a>
          </section>
        )
      )}
    </TakerShell>
  );
}
